import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

// Theme for user list panel
const USER_PANEL_THEME = {
  background: '#0c0c12',
  borderColor: '#1a1a24',
  accent: '#5a6f8e',
  textPrimary: '#e8e8e8',
  textSecondary: '#888',
  success: '#4a9d6b',
  warning: '#c9a227',
  danger: '#dc3545'
};

const Sidebar = ({ activeTab, setActiveTab, onManageBilling, isCustomer = false, networkUsers = [], networkUsersLoading = false, selectedNetworkUser, setSelectedNetworkUser, selectedApiUser, setSelectedApiUser, collapsed = false, setCollapsed }) => {
  // Use internal state only if parent doesn't control it
  const [internalCollapsed, setInternalCollapsed] = useState(false);
  const isCollapsed = setCollapsed ? collapsed : internalCollapsed;
  const toggleCollapsed = setCollapsed ? setCollapsed : setInternalCollapsed;
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const tier = user?.tier || 'free';
  const isAdmin = user?.is_admin || false;
  const displayTier = isAdmin ? 'admin' : tier;

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  let menuItems = [];

  if (isCustomer) {
    // Customer menu - only show Sybil, Graph, and API
    menuItems = [
      {
        id: 'sybil',
        label: 'Sybil Detection',
        icon: 'bi-shield-exclamation',
        description: 'Your contract data'
      },
      {
        id: 'graph',
        label: 'Sybil Clusters',
        icon: 'bi-diagram-3',
        description: 'Visual analysis'
      },
      {
        id: 'api',
        label: 'API & Docs',
        icon: 'bi-code-square',
        description: 'API access'
      }
    ];
  } else {
    // Admin/full menu
    menuItems = [
      {
        id: 'eikon',
        label: 'Eikon',
        icon: 'bi-diagram-2',
        description: 'Relation knowledge graph'
      },
      {
        id: 'console',
        label: 'Query Console',
        icon: 'bi-terminal',
        description: 'Natural language queries'
      },
      {
        id: 'sybil',
        label: 'Sybil Defender',
        icon: 'bi-shield-exclamation',
        description: 'Fraud detection'
      },
      {
        id: 'network',
        label: 'User Dashboards',
        icon: 'bi-person-badge',
        description: 'Customer dashboard views'
      },
      {
        id: 'plume',
        label: 'Plume Dashboard',
        icon: 'bi-globe2',
        description: 'Plume network analysis'
      },
      {
        id: 'graph',
        label: 'Graph Explorer',
        icon: 'bi-diagram-3',
        description: 'Visual graph analysis'
      },
      {
        id: 'api',
        label: 'API & Docs',
        icon: 'bi-code-square',
        description: 'Documentation'
      }
    ];

    // Admin functionality is now combined with User Dashboards tab
  }

  return (
    <div
      className={`d-flex flex-column bg-black border-end border-secondary ${isCollapsed ? 'sidebar-collapsed' : 'sidebar-expanded'}`}
      style={{
        width: isCollapsed ? '80px' : '260px',
        minHeight: '100vh',
        position: 'fixed',
        left: 0,
        top: 0,
        transition: 'width 0.3s ease',
        zIndex: 1000
      }}
    >
      {/* Header */}
      <div className="p-3 border-bottom border-secondary">
        <div className="d-flex align-items-center justify-content-between">
          {!isCollapsed && (
            <div className="d-flex align-items-center">
              <span className="fw-bold text-white" style={{ fontSize: '1.1rem', letterSpacing: '-0.02em' }}>frwd labs</span>
            </div>
          )}
          {isCollapsed && (
            <span className="fw-bold mx-auto" style={{ fontSize: '1.1rem', color: '#5a6f8e', letterSpacing: '-0.02em' }}>f</span>
          )}
          <button
            className="btn btn-link text-secondary p-0 border-0"
            onClick={() => toggleCollapsed(!isCollapsed)}
            style={{ fontSize: '1.2rem' }}
          >
            <i className={`bi ${isCollapsed ? 'bi-chevron-right' : 'bi-chevron-left'}`}></i>
          </button>
        </div>
      </div>

      {/* Navigation Items */}
      <nav className="flex-grow-1 py-3">
        <ul className="list-unstyled px-2">
          {menuItems.map((item) => (
            <React.Fragment key={item.id}>
              <li className="mb-1">
                <button
                  className={`btn w-100 text-start d-flex align-items-center ${activeTab === item.id
                    ? 'btn-primary'
                    : 'btn-link text-secondary'
                    } ${isCollapsed ? 'justify-content-center' : ''}`}
                  onClick={() => setActiveTab(item.id)}
                  style={{
                    borderRadius: '8px',
                    padding: isCollapsed ? '12px 0' : '12px 16px',
                    textDecoration: 'none',
                    border: 'none',
                    transition: 'all 0.2s ease'
                  }}
                  title={isCollapsed ? item.label : ''}
                >
                  {!isCollapsed && (
                    <div className="flex-grow-1">
                      <div className="fw-medium">{item.label}</div>
                      <small className="opacity-75">{item.description}</small>
                    </div>
                  )}
                  {isCollapsed && (
                    <div className="fw-bold" style={{ fontSize: '0.9rem', letterSpacing: '-0.02em', textTransform: 'lowercase' }}>{item.label.substring(0, 1).toLowerCase()}</div>
                  )}
                </button>
              </li>

              {/* User Dashboards - appears directly under Network Explorer when active */}
              {item.id === 'network' && activeTab === 'network' && !isCollapsed && (
                <li className="mb-1">
                  <div
                    className="user-dashboards-panel"
                    style={{
                      marginLeft: '0.75rem',
                      marginRight: '0.25rem',
                      marginTop: '0.25rem',
                      marginBottom: '0.5rem',
                      padding: '0.5rem',
                      backgroundColor: USER_PANEL_THEME.background,
                      borderRadius: '6px',
                      border: `1px solid ${USER_PANEL_THEME.borderColor}`,
                      borderLeft: `3px solid ${USER_PANEL_THEME.accent}`,
                      maxHeight: '250px',
                      overflowY: 'auto',
                      overflowX: 'hidden'
                    }}>
                    <div style={{
                      color: USER_PANEL_THEME.textSecondary,
                      fontSize: '0.55rem',
                      marginBottom: '0.5rem'
                    }}>
                      {networkUsers.length} user{networkUsers.length !== 1 ? 's' : ''}
                    </div>

                    {networkUsersLoading ? (
                      <div className="text-center py-2">
                        <div className="spinner-border spinner-border-sm" style={{ color: USER_PANEL_THEME.accent, width: '0.75rem', height: '0.75rem' }}></div>
                      </div>
                    ) : networkUsers.length === 0 ? (
                      <div style={{ color: USER_PANEL_THEME.textSecondary, fontSize: '0.65rem' }}>
                        No users found
                      </div>
                    ) : (
                      networkUsers.map((u) => (
                        <div
                          key={u.id}
                          onClick={() => setSelectedNetworkUser && setSelectedNetworkUser(selectedNetworkUser === u.id ? null : u.id)}
                          style={{
                            padding: '0.4rem',
                            marginBottom: '0.2rem',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            backgroundColor: selectedNetworkUser === u.id
                              ? `rgba(90, 111, 142, 0.3)`
                              : 'transparent',
                            border: selectedNetworkUser === u.id
                              ? `1px solid ${USER_PANEL_THEME.accent}`
                              : '1px solid transparent',
                            transition: 'all 0.15s ease'
                          }}
                        >
                          <div className="d-flex align-items-center">
                            <div style={{
                              width: '20px',
                              height: '20px',
                              borderRadius: '50%',
                              backgroundColor: u.is_admin ? USER_PANEL_THEME.danger : USER_PANEL_THEME.accent,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              color: 'white',
                              fontWeight: 'bold',
                              fontSize: '0.55rem',
                              marginRight: '0.4rem',
                              flexShrink: 0
                            }}>
                              {u.email?.charAt(0).toUpperCase() || 'U'}
                            </div>
                            <div style={{ overflow: 'hidden', flex: 1 }}>
                              <div style={{
                                color: USER_PANEL_THEME.textPrimary,
                                fontSize: '0.65rem',
                                fontWeight: 500,
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis'
                              }}>
                                {u.email}
                              </div>
                              <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.25rem'
                              }}>
                                <span style={{
                                  backgroundColor: u.is_admin ? USER_PANEL_THEME.danger :
                                    u.tier === 'enterprise' ? USER_PANEL_THEME.warning : '#333',
                                  color: u.is_admin || u.tier === 'enterprise' ? 'white' : USER_PANEL_THEME.textSecondary,
                                  padding: '0.05rem 0.2rem',
                                  borderRadius: '2px',
                                  fontSize: '0.45rem',
                                  fontWeight: 600,
                                  textTransform: 'uppercase'
                                }}>
                                  {u.is_admin ? 'admin' : u.tier || 'free'}
                                </span>
                                {u.has_active_session && (
                                  <div style={{
                                    width: '5px',
                                    height: '5px',
                                    borderRadius: '50%',
                                    backgroundColor: USER_PANEL_THEME.success
                                  }} title="Online"></div>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </li>
              )}

              {/* API User List - appears directly under API & Docs when active */}
              {item.id === 'api' && activeTab === 'api' && !isCollapsed && (
                <li className="mb-1">
                  <div
                    className="user-dashboards-panel"
                    style={{
                      marginLeft: '0.75rem',
                      marginRight: '0.25rem',
                      marginTop: '0.25rem',
                      marginBottom: '0.5rem',
                      padding: '0.5rem',
                      backgroundColor: USER_PANEL_THEME.background,
                      borderRadius: '6px',
                      border: `1px solid ${USER_PANEL_THEME.borderColor}`,
                      borderLeft: `3px solid ${USER_PANEL_THEME.accent}`,
                      maxHeight: '250px',
                      overflowY: 'auto',
                      overflowX: 'hidden'
                    }}>
                    <div style={{
                      color: USER_PANEL_THEME.textSecondary,
                      fontSize: '0.55rem',
                      marginBottom: '0.5rem'
                    }}>
                      {networkUsers.length} user{networkUsers.length !== 1 ? 's' : ''}
                    </div>

                    {networkUsersLoading ? (
                      <div className="text-center py-2">
                        <div className="spinner-border spinner-border-sm" style={{ color: USER_PANEL_THEME.accent, width: '0.75rem', height: '0.75rem' }}></div>
                      </div>
                    ) : networkUsers.length === 0 ? (
                      <div style={{ color: USER_PANEL_THEME.textSecondary, fontSize: '0.65rem' }}>
                        No users found
                      </div>
                    ) : (
                      networkUsers.map((u) => (
                        <div
                          key={u.id}
                          onClick={() => setSelectedApiUser && setSelectedApiUser(selectedApiUser === u.id ? null : u.id)}
                          style={{
                            padding: '0.4rem',
                            marginBottom: '0.2rem',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            backgroundColor: selectedApiUser === u.id
                              ? `rgba(90, 111, 142, 0.3)`
                              : 'transparent',
                            border: selectedApiUser === u.id
                              ? `1px solid ${USER_PANEL_THEME.accent}`
                              : '1px solid transparent',
                            transition: 'all 0.15s ease'
                          }}
                        >
                          <div className="d-flex align-items-center">
                            <div style={{
                              width: '20px',
                              height: '20px',
                              borderRadius: '50%',
                              backgroundColor: u.is_admin ? USER_PANEL_THEME.danger : USER_PANEL_THEME.accent,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              color: 'white',
                              fontWeight: 'bold',
                              fontSize: '0.55rem',
                              marginRight: '0.4rem',
                              flexShrink: 0
                            }}>
                              {u.email?.charAt(0).toUpperCase() || 'U'}
                            </div>
                            <div style={{ overflow: 'hidden', flex: 1 }}>
                              <div style={{
                                color: USER_PANEL_THEME.textPrimary,
                                fontSize: '0.65rem',
                                fontWeight: 500,
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis'
                              }}>
                                {u.email}
                              </div>
                              <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.25rem'
                              }}>
                                <span style={{
                                  backgroundColor: u.is_admin ? USER_PANEL_THEME.danger :
                                    u.tier === 'enterprise' ? USER_PANEL_THEME.warning : '#333',
                                  color: u.is_admin || u.tier === 'enterprise' ? 'white' : USER_PANEL_THEME.textSecondary,
                                  padding: '0.05rem 0.2rem',
                                  borderRadius: '2px',
                                  fontSize: '0.45rem',
                                  fontWeight: 600,
                                  textTransform: 'uppercase'
                                }}>
                                  {u.is_admin ? 'admin' : u.tier || 'free'}
                                </span>
                                {u.has_active_session && (
                                  <div style={{
                                    width: '5px',
                                    height: '5px',
                                    borderRadius: '50%',
                                    backgroundColor: USER_PANEL_THEME.success
                                  }} title="Online"></div>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </li>
              )}
            </React.Fragment>
          ))}
        </ul>

        {/* Divider */}
        <hr className="border-secondary mx-3 my-3" />

        {/* Additional Actions */}
        <ul className="list-unstyled px-2">
          {/* Only show pricing for non-customers (admins and public users) */}
          {!isCustomer && (
            <li className="mb-1">
              <a
                href="/pricing.html#/pricing"
                className={`btn btn-link text-secondary w-100 text-start d-flex align-items-center ${isCollapsed ? 'justify-content-center' : ''
                  }`}
                style={{
                  borderRadius: '8px',
                  padding: isCollapsed ? '12px 0' : '12px 16px',
                  textDecoration: 'none',
                  border: 'none'
                }}
                title={isCollapsed ? 'Pricing' : ''}
              >
                <i className="bi bi-tag" style={{ fontSize: '1.3rem' }}></i>
                {!isCollapsed && <span className="ms-3">Pricing</span>}
              </a>
            </li>
          )}
          <li className="mb-1">
            <a
              href="mailto:support@frwdlabs.xyz"
              className={`btn btn-link text-secondary w-100 text-start d-flex align-items-center ${isCollapsed ? 'justify-content-center' : ''
                }`}
              style={{
                borderRadius: '8px',
                padding: isCollapsed ? '12px 0' : '12px 16px',
                textDecoration: 'none',
                border: 'none'
              }}
              title={isCollapsed ? 'Support' : ''}
            >
              <i className="bi bi-question-circle" style={{ fontSize: '1.3rem' }}></i>
              {!isCollapsed && <span className="ms-3">Support</span>}
            </a>
          </li>
        </ul>
      </nav>

      {/* User Profile & Settings */}
      <div className="border-top border-secondary p-3">
        {/* User Info */}
        {!isCollapsed && (
          <div className="mb-3">
            <div className="d-flex align-items-center mb-2">
              <div className="rounded-circle bg-dark border border-secondary d-flex align-items-center justify-content-center"
                style={{ width: '40px', height: '40px' }}>
                <span className="text-secondary" style={{ fontSize: '0.8rem' }}>U</span>
              </div>
              <div className="ms-3 flex-grow-1 overflow-hidden">
                <div className="text-white fw-medium text-truncate">{user?.email}</div>
                {/* For customers, show org name. For admins, show tier badge */}
                {isCustomer ? (
                  user?.org && (
                    <div className="text-secondary small text-truncate">
                      {user.org}
                    </div>
                  )
                ) : (
                  <div>
                    <span className={`badge ${displayTier === 'admin' ? 'bg-danger text-white' :
                      displayTier === 'enterprise' ? 'bg-warning text-dark' :
                        displayTier === 'basic' ? 'bg-primary' :
                          'bg-secondary'
                      }`} style={{ fontSize: '0.7rem' }}>
                      {displayTier.toUpperCase()}
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {isCollapsed && (
          <div className="text-center mb-3">
            <div className="rounded-circle d-flex align-items-center justify-content-center mx-auto"
              style={{ width: '40px', height: '40px', backgroundColor: '#5a6f8e' }}>
              <i className="bi bi-person-fill text-white"></i>
            </div>
          </div>
        )}

        {/* Action Buttons - hide upgrade/billing for customers */}
        <div className={`d-flex gap-2 ${isCollapsed ? 'flex-column align-items-center' : ''}`}>
          {/* Only show upgrade button for non-customer free tier users */}
          {!isCustomer && tier === 'free' && !isAdmin && !isCollapsed && (
            <button
              className="btn btn-warning btn-sm flex-grow-1"
              onClick={() => window.location.href = '/pricing.html#/pricing'}
            >
              <i className="bi bi-arrow-up-circle me-1"></i>
              Upgrade
            </button>
          )}

          {/* Only show billing button for non-customer paid users */}
          {!isCustomer && tier !== 'free' && tier !== 'admin' && !isAdmin && onManageBilling && !isCollapsed && (
            <button
              className="btn btn-outline-secondary btn-sm flex-grow-1"
              onClick={onManageBilling}
            >
              <i className="bi bi-gear me-1"></i>
              Billing
            </button>
          )}

          <button
            className={`btn btn-outline-secondary btn-sm ${isCollapsed ? '' : 'flex-grow-1'}`}
            onClick={handleLogout}
            title={isCollapsed ? 'Logout' : ''}
          >
            {!isCollapsed && <span>Logout</span>}
          </button>
        </div>
      </div>
    </div>
  );
};

export default Sidebar;


